// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Breadcrumbs } from './../../components/Breadcrumbs/Breadcrumbs';

import { useTranslation } from 'react-i18next';

import BusySpinner from './../../components/BusySpinner';
import Header from './../../components/Header';
import Footer from './../../components/Footer';
import history from 'src/history';
import allRoutes from 'src/views';
import { SERIAL_NUMBER } from './../../views/deviceDetails';
import { deviceSelector } from './../../store/deviceInfo/selectors';
import { setHiddenAnnouncement } from './../../store/hiddenAnnouncement';

import Styles from './commonLayout.module.scss';

interface SkipLink {
    key: string;
    text: string;
}

interface Props {
    children: JSX.Element;
    hideBreadcrumb?: boolean;
    loading?: boolean;
    loadingMessage?: string;
    pageTitle?: string | string[];
    skipLinks?: SkipLink[];
}

export default function CommonLayout(props: Props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const {
        pageTitle,
    } = props;

    const device = useSelector(state => deviceSelector(state));

    const routes = allRoutes.map((route) => ({
        ...route,
        crumbText: t(route.crumbText).replace(SERIAL_NUMBER, device.serialNumber),
    }));

    // Make an array of string (or undefined) parts representing the page title
    const pageTitleDependencies: (string | undefined)[] = ['ROBO', ...(Array.isArray(pageTitle) ? pageTitle : [pageTitle])];
    React.useEffect(() => {
        const title: string = pageTitleDependencies.filter((part: string | undefined) => part).join(' > ');
        if (title !== document.title) {
            document.title = title;
            dispatch(setHiddenAnnouncement({
                message: title.replace(/ > /g, ' '), // Strip out the > so narrator doesn't say "Greater" on page transitions
            }));
        }
    }, pageTitleDependencies);

    return props.loading ? <BusySpinner messageKey={props.loadingMessage || ''} /> : (
        <div className={Styles.commonLayout}>
            <SkipLinks skipLinks={props.skipLinks} />
            <Header />
            {!props.hideBreadcrumb && <div className={Styles.breadcrumbs}>
                <Breadcrumbs routes={routes} history={history} />
            </div>}
            <div id="main" role="main" className={Styles.commonLayoutContent}>
                {props.children}
            </div>
            <Footer />
        </div>
    );
}

// allow user to skip to specific section on the page
// an element has to set its id to link.key to skip to the section
function SkipLinks({ skipLinks = [] }: { skipLinks?: SkipLink[] }) {
    const { t } = useTranslation();
    const [isFocused, setFocused] = React.useState(false);
    const links = [{
        key: 'main',
        text: t('main'),
    }];

    const onFocus = () => setFocused(true);
    const onBlur = () => setFocused(false);

    const skipLinksClass = isFocused ? Styles.focusWithin : Styles.skipLinks;

    return (
        <div className={skipLinksClass}>
            {
                links.map((link: SkipLink, key: number) => (
                    <a
                        className={Styles.skipLink}
                        href={`#${link.key}`}
                        key={key}
                        onBlur={onBlur}
                        onFocus={onFocus}
                    >{t('skip_to', { link: link.text })}</a>
                ))
            }
        </div>
    );
}